import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Card, Button, Input, Textarea, Badge, Alert, LoadingSpinner, Modal } from '../common';
import { Settings, Save, RefreshCw, Download, Upload, Shield, Zap, Globe } from 'lucide-react';

const SystemSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState('');

  const categories = [
    { key: 'all', label: 'All Settings', icon: Settings },
    { key: 'rate_limiting', label: 'Rate Limiting', icon: Shield },
    { key: 'security', label: 'Security', icon: Zap },
    { key: 'application', label: 'Application', icon: Globe }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminAPI.getSettings();
      if (response.success) {
        setSettings(response.data);
      } else {
        setError(response.error || 'Failed to load system settings');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (settingKey, newValue) => {
    setSettings(prevSettings =>
      prevSettings.map(setting =>
        setting.setting_key === settingKey
          ? { ...setting, setting_value: newValue }
          : setting
      )
    );
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const changedSettings = settings
        .filter(setting => setting.is_editable)
        .map(setting => ({
          key: setting.setting_key,
          value: setting.setting_value,
          description: setting.description
        }));

      const response = await adminAPI.updateSettings({ settings: changedSettings });
      if (!response.success) {
        throw new Error(response.error || 'Failed to save settings');
      }
      
      setSuccess('Settings saved successfully');
      await loadSettings(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await adminAPI.resetRateLimitSettings();
      if (!response.success) {
        throw new Error(response.error || 'Failed to reset settings');
      }
      
      setSuccess('Settings reset to defaults successfully');
      await loadSettings();
    } catch (error) {
      console.error('Error resetting settings:', error);
      setError('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const exportSettings = async (format = 'json') => {
    try {
      const response = await adminAPI.exportSettings({ format });

      if (format === 'csv') {
        // Download CSV file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'system_settings.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        // Download JSON file
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'system_settings.json');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }

      setSuccess('Settings exported successfully');
    } catch (error) {
      console.error('Error exporting settings:', error);
      setError('Failed to export settings');
    }
  };

  const importSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const settingsData = JSON.parse(importData);
      const response = await adminAPI.importSettings({ settings: settingsData });
      if (!response.success) {
        throw new Error(response.error || 'Failed to import settings');
      }
      
      setSuccess('Settings imported successfully');
      setShowImportModal(false);
      setImportData('');
      await loadSettings();
    } catch (error) {
      console.error('Error importing settings:', error);
      setError('Failed to import settings. Please check the JSON format.');
    } finally {
      setSaving(false);
    }
  };

  const filteredSettings = selectedCategory === 'all' 
    ? settings 
    : settings.filter(setting => setting.category === selectedCategory);

  const renderSettingValue = (setting) => {
    const { setting_key, setting_value, setting_type, is_editable } = setting;

    if (!is_editable) {
      return (
        <div className="flex items-center space-x-2">
          <span className="text-ink-600">{setting_value}</span>
          <Badge variant="secondary" size="sm">Read Only</Badge>
        </div>
      );
    }

    switch (setting_type) {
      case 'boolean':
        return (
          <select
            value={setting_value}
            onChange={(e) => handleSettingChange(setting_key, e.target.value)}
            className="px-3 py-2 border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={setting_value}
            onChange={(e) => handleSettingChange(setting_key, e.target.value)}
            className="w-32"
          />
        );
      
      default:
        return (
          <Input
            type="text"
            value={setting_value}
            onChange={(e) => handleSettingChange(setting_key, e.target.value)}
            className="w-full"
          />
        );
    }
  };

  const formatValue = (value, type) => {
    if (type === 'number') {
      const num = parseInt(value);
      if (value.includes('60000')) return `${num / 1000}s`;
      if (value.includes('900000')) return `${num / 60000}m`;
      if (value.includes('3600000')) return `${num / 3600000}h`;
      return num.toLocaleString();
    }
    return value;
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <LoadingSpinner size="lg" />
        <p className="text-ink-500 mt-4 text-sm">Loading system settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">System Settings</h1>
          <p className="text-ink-600 mt-1">Manage system configuration and rate limits</p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Export Buttons Group */}
          <div className="flex items-center bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-1 border border-blue-100 shadow-sm">
            <Button
              onClick={() => exportSettings('json')}
              variant="ghost"
              size="sm"
              icon={Download}
              className="text-blue-700 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-all duration-200 font-medium"
            >
              Export JSON
            </Button>
            <div className="w-px h-6 bg-gradient-to-b from-blue-200 to-green-200 mx-1"></div>
            <Button
              onClick={() => exportSettings('csv')}
              variant="ghost"
              size="sm"
              icon={Download}
              className="text-green-700 hover:text-green-800 hover:bg-green-100 rounded-lg transition-all duration-200 font-medium"
            >
              Export CSV
            </Button>
          </div>

          {/* Import Button */}
          <Button
            onClick={() => setShowImportModal(true)}
            variant="outline"
            size="sm"
            icon={Upload}
            className="border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400 hover:text-purple-800 transition-all duration-200 font-medium shadow-sm"
          >
            Import Settings
          </Button>

          {/* Reset Button */}
          <Button
            onClick={resetToDefaults}
            variant="outline"
            size="sm"
            icon={RefreshCw}
            disabled={saving}
            className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-800 disabled:opacity-50 transition-all duration-200 font-medium shadow-sm"
          >
            Reset to Defaults
          </Button>

          {/* Save Button */}
          <Button
            onClick={saveSettings}
            variant="primary"
            size="sm"
            icon={Save}
            disabled={saving}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all duration-200 font-medium text-white"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Success</h3>
              <p className="text-sm text-green-700 mt-1">{success}</p>
            </div>
          </div>
        </Alert>
      )}

      {/* Category Filter */}
      <div className="bg-gradient-to-r from-gray-50 to-white border border-ink-200 rounded-xl p-1 shadow-sm">
        <div className="flex space-x-1">
          {categories.map(category => {
            const Icon = category.icon;
            const isActive = selectedCategory === category.key;
            return (
              <Button
                key={category.key}
                onClick={() => setSelectedCategory(category.key)}
                variant={isActive ? 'primary' : 'ghost'}
                size="sm"
                icon={Icon}
                className={`${
                  isActive 
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg font-medium' 
                    : 'text-ink-600 hover:text-ink-900 hover:bg-white hover:shadow-sm font-medium'
                } transition-all duration-200 rounded-lg`}
              >
                {category.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid gap-6">
        {filteredSettings.map(setting => (
          <Card key={setting.setting_key} className="p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <h3 className="text-lg font-semibold text-ink-900">
                    {setting.setting_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </h3>
                  <Badge 
                    variant={setting.category === 'rate_limiting' ? 'primary' : 'secondary'}
                    className={`${
                      setting.category === 'rate_limiting' 
                        ? 'bg-blue-100 text-blue-800' 
                        : setting.category === 'security'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {setting.category}
                  </Badge>
                  {setting.setting_type && (
                    <Badge 
                      variant="outline" 
                      size="sm"
                      className="bg-ink-50 text-ink-600 border-ink-200"
                    >
                      {setting.setting_type}
                    </Badge>
                  )}
                </div>
                
                {setting.description && (
                  <p className="text-ink-600 mb-4 text-sm leading-relaxed">{setting.description}</p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-2">
                      Current Value
                    </label>
                    {renderSettingValue(setting)}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-2">
                      Formatted Value
                    </label>
                    <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border border-ink-200 rounded-lg font-mono text-sm">
                      {formatValue(setting.setting_value, setting.setting_type)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredSettings.length === 0 && (
        <Card className="p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-ink-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Settings className="w-8 h-8 text-ink-400" />
            </div>
            <h3 className="text-lg font-medium text-ink-900 mb-2">No Settings Found</h3>
            <p className="text-ink-500 mb-4">
              No settings found for the selected category. Try selecting a different category or check if settings are properly configured.
            </p>
            <Button
              onClick={() => setSelectedCategory('all')}
              variant="outline"
              size="sm"
              className="border-ink-300 text-ink-700 hover:bg-ink-50"
            >
              View All Settings
            </Button>
          </div>
        </Card>
      )}

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Settings"
        size="lg"
      >
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <Upload className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Import Settings</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Paste the JSON settings data below. This will update existing settings and create new ones. Make sure the JSON format is valid.
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Settings JSON Data
            </label>
            <Textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder='{"exportDate": "2025-08-19T...", "settings": [...]}'
              rows={12}
              className="font-mono text-sm border-ink-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button
              onClick={() => setShowImportModal(false)}
              variant="outline"
              className="border-ink-300 text-ink-700 hover:bg-ink-50"
            >
              Cancel
            </Button>
            <Button
              onClick={importSettings}
              variant="primary"
              disabled={saving || !importData.trim()}
              className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 shadow-sm"
            >
              {saving ? 'Importing...' : 'Import Settings'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SystemSettings;
